import { CHARGE_CARD_ONFILE_FAILURE, CHARGE_CARD_ONFILE_REQUEST, CHARGE_CARD_ONFILE_SUCCESS } from 'actions/payment/beChargeCardOnFile.action';
import feChangePaymentOption from 'actions/payment/feChangePaymentOption.action';
import { CHANGE_PAYMENT_TYPE } from 'actions/payment/feChangePaymentType.types';
import feDelPosIntentId from 'actions/pos/feDelPosIntentId.action';
import feSetPosIntentId from 'actions/pos/feSetPosIntentId.action';
import { aTypeInitCheckoutData } from 'actions/shared.types';
import { feCleanCheckoutData, feSetCheckoutInitDataFromClientProfile } from 'actions/ui.actions';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import DEPOSIT_ACTION from 'constants/payment/depositAction.const';
import getAny from 'utils/accessors/getAny.util';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromActionToFtag from 'utils/converters/log/fromActionToFtag.util';
import { nowAsIso } from 'utils/date/now.util';
import { formatCurrency } from 'utils/formatters/currency.util';
import nullBind from 'utils/functions/nullBind.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFn from 'utils/predicates/function/isFn.util';

import { CHANGE_APPOINTMENT_STATUS_SUCCESS } from 'booking/constants';
import { priceForStylist } from 'booking/overrides';
import { GET_TRANSACTIONS_BY_STYLIST_SUCCESS } from 'cashup/actions';
import {
  ADD_CLIENT,
  ADD_CLIENT_DEPOSITS,
  ADD_CLIENT_SEARCH_DEPOSITS,
  ADD_PRODUCT,
  ADD_SERVICE,
  APPOINTMENT_FAILURE,
  APPOINTMENT_REQUEST,
  APPOINTMENT_SUCCESS,
  CANCEL_SLICK_CARD_ONLINE,
  CHANGE_ADDITIONAL_DISCOUNT_TYPE,
  CHANGE_PRODUCT_DISCOUNT_TYPE,
  CHANGE_SERVICE_DISCOUNT_TYPE,
  CHANGE_TIP_TYPE,
  CHECKOUT_ADD_PRODUCT_FROM_SCAN,
  CHECKOUT_CLIENT_FAILURE,
  CHECKOUT_CLIENT_REQUEST,
  CHECKOUT_CLIENT_SUCCESS,
  CHECKOUT_FETCH_ALL_DEPOSITS_FAILURE,
  CHECKOUT_FETCH_ALL_DEPOSITS_REQUEST,
  CHECKOUT_FETCH_ALL_DEPOSITS_SUCCESS,
  CHECKOUT_FETCH_DEPOSIT_FAILURE,
  CHECKOUT_FETCH_DEPOSIT_REQUEST,
  CHECKOUT_FETCH_DEPOSIT_SUCCESS,
  CHECKOUT_INIT_DEPOSIT_REQUEST,
  CHECKOUT_INIT_DEPOSIT_SUCCESS,
  CHECKOUT_START_SESSION_SUCCESS,
  CLEAN_TRANSACTION_DATA,
  CLEAR_TRANSACTION_LOG,
  DEPOSIT_CLIENT_LIST_REQUEST,
  DEPOSIT_CLIENT_LIST_SUCCESS,
  EXPORT_TRANSACTION_FAILURE,
  EXPORT_TRANSACTION_REQUEST,
  EXPORT_TRANSACTION_SUCCESS,
  FETCH_NEXT_APPOINTMENT_SUCCESS,
  REMOVE_CLIENT,
  REMOVE_PRODUCT,
  REMOVE_SERVICE,
  SET_CHECKOUT_DATA,
  SET_CHECKOUT_INIT_DATA_FROM_CASHUP,
  SET_CHECKOUT_INIT_DATA_FROM_MAIN_NAV,
  SET_CHECKOUT_SALON_TAXES_AND_FEES,
  SET_CHECKOUT_TOTAL,
  TOGGLE_CHECKOUT_BUTTONS_ENABLED,
  TRANSACTION_DETAILS_FAILURE,
  TRANSACTION_DETAILS_REQUEST,
  TRANSACTION_DETAILS_SUCCESS,
  UNLOCK_DEPOSIT_FAILURE,
  UNLOCK_DEPOSIT_REQUEST,
  UNLOCK_DEPOSIT_SUCCESS,
  UPDATE_ADDITIONAL_DISCOUNT,
  UPDATE_ADDITIONAL_DISCOUNT_REASON,
  UPDATE_CHECKOUT_COMPLETE,
  UPDATE_DEPOSIT_FROM_CHECKOUT,
  UPDATE_OTHER_AMOUNT,
  UPDATE_PART_CASH,
  UPDATE_PRODUCT_DISCOUNT,
  UPDATE_PRODUCT_PRICE,
  UPDATE_PRODUCT_QUANTITY,
  UPDATE_PRODUCT_STYLIST_ID,
  UPDATE_PRODUCT_TITLE,
  UPDATE_SERVICE_DISCOUNT,
  UPDATE_SERVICE_PRICE,
  UPDATE_SERVICE_STYLIST_ID,
  UPDATE_TIP_AMOUNT,
  UPDATE_VOUCHERS_USED,
} from 'checkout/constants';
import reduceInitDepositSuccess from 'checkout/reducers/reduceInitDepositSuccess';

const L = logger('checkoutReducer');

const getCardAmount = nullBind(getAnyOrVoid, 'data.cardAmount');

// SEE: https://github.com/getslick/api.core/blob/staging/docs/pos.md
const reduceSetPosIntentId = (state, action) => {
  const FTAG = fromActionToFtag(action);

  // whole data object is sent to BE verbatim, so it must have null if the intentId is missing
  const intentId = fromAnyToString(action.data) || null;
  L.debug(FTAG, nowAsIso(), intentId);

  return {
    ...state,
    data: { ...asObject(state.data), intent_id: intentId },
    pos: { ...asObject(state.pos), intentId, amount: getCardAmount(state) },
  };
};

// SEE: https://getslick.atlassian.net/browse/GS-11856
const reduceDelPosIntentId = (state, action) => {
  const FTAG = fromActionToFtag(action);
  L.debug(FTAG, nowAsIso());
  return {
    ...state,
    data: { ...asObject(state.data), intent_id: null },
    pos: { ...asObject(state.pos), intentId: null },
  };
};

const MAP = Object.freeze({
  [feSetPosIntentId.AT.name]: reduceSetPosIntentId,
  [feDelPosIntentId.AT.name]: reduceDelPosIntentId,
  [feChangePaymentOption.AT.name]: (state, { data }) => ({ ...state, selectedPaymentOption: data }),
});

export const initState = () => ({
  pos: {},
  data: {
    clients: [],
    deposits: {
      clientDeposits: [],
      isFetching: false,
      errorMessage: '',
    },
    additionalDiscount: 0,
    additionalDiscountType: 'absolute',
    additionalDiscountReason: '',
    partCash: 0,
    total: 0,
    balanceToPay: 0,
    tipAmount: 0,
    tipType: 'absolute',
    paymentType: 'cash',
    otherAmount: 0,
    voucherId: null,
    cardAmount: 0,
    onlineAmount: 0,
    salonTaxesAndFees: {
      taxes: [],
      fees: [],
    },
    is_no_show_fee: false,
    is_void: false,
    void_created: false,
    effective_date: null,
    active_card: null,
    depositListData: [],
    vouchers_used: [],
  },
  checkoutLoaded: false,
  checkoutPayButtonsDisabled: false,
  defaultStylistId: null,
  bookingData: {},
  appointments: [],
  clientId: null,
  clients: [],
  isFetching: false,
  errorMessage: '',
  selectedAppointment: null,
  initialAppointment: null,
  finishUrl: '',
  edit_client: {
    isFetching: false,
    error: null,
    data: null,
  },
  initialDeposit: {
    isFetching: false,
    response: null,
    error: null,
  },
  allProceduresForTheDay: [],
  clientsDeposits: [],
  cardOnFileChargeSuccess: false,
  chargingCardError: '',
  depositUnlock: null,
  transactionLog: null,
  depositClientListSuccess: false,
  depositCreatedForCheckout: {},
});

const calculateTotal = (data) => {
  let total = 0;

  for (const client of asArray(data.clients)) {
    total += [...client.services].reduce((a, b) => a + b.finalPrice, 0);
    total += [...client.products].reduce((a, b) => a + b.finalPrice, 0);
  }

  const a = data.additionalDiscount;
  total -= data.additionalDiscountType === 'absolute' ? a : total * (a / 100);

  const t = data.tipAmount;
  total += parseFloat(data.tipType === 'absolute' ? t : (t / 100) * total);

  return parseFloat(total.toFixed(2));
};

const isSelectedDeposit = (deposit) => deposit?.selected === true;

const calculateBalance = (data) => {
  let total = data.total;

  for (const selectedDeposit of data.depositListData.filter(isSelectedDeposit)) {
    total -= selectedDeposit.use_deposit;
  }

  const vouchersUsed = data.vouchers_used;
  if (vouchersUsed) {
    for (let i = 0; i < vouchersUsed.length; i += 1) {
      if (vouchersUsed[i]) {
        total -= vouchersUsed[i].used_amount;
      }
    }
  }

  return parseFloat(total.toFixed(2));
};

export const calculateCheckoutTaxesAndFees = ({ clients, services, products, taxes, fees }) => {
  const serviceTaxes = taxes.filter((tax) => tax.applied_to === 'services' || tax.applied_to === 'products_and_services');
  const serviceFees = fees.filter((fee) => fee.applied_to === 'services' || fee.applied_to === 'products_and_services');
  const productTaxes = taxes.filter((tax) => tax.applied_to === 'products' || tax.applied_to === 'products_and_services');
  const productFees = fees.filter((fee) => fee.applied_to === 'products' || fee.applied_to === 'products_and_services');
  const getServiceTaxById = (id) => asArray(serviceTaxes).find((tax) => tax.id === id);
  const getServiceFeeById = (id) => asArray(serviceFees).find((fee) => fee.id === id);
  const getProductTaxById = (id) => asArray(productTaxes).find((tax) => tax.id === id);
  const getProductFeeById = (id) => asArray(productFees).find((fee) => fee.id === id);

  const servicesInCheckout = asArray(clients).flatMap((client) => client.services) ?? [];

  const checkoutTaxes = [];
  const checkoutFees = [];
  const charges = {
    taxes: [],
    fees: [],
    tax_exempts: {
      products: [],
      services: [],
    },
  };

  const getOrCreateTaxCharge = (tax) => {
    let entry = charges.taxes.find((c) => c.tax_id === tax.id);
    if (!entry) {
      entry = { tax_id: tax.id, tax_name: tax.name, tax_percentage: tax.percentage, total_tax_amount: 0, charged_services: [], charged_products: [] };
      charges.taxes.push(entry);
    }
    return entry;
  };

  const getOrCreateFeeCharge = (fee) => {
    let entry = charges.fees.find((c) => c.fee_id === fee.id);
    if (!entry) {
      entry = { fee_id: fee.id, fee_name: fee.name, fee_percentage: fee.percentage, fee_amount: 0, stylists_with_fee: {} };
      charges.fees.push(entry);
    }
    return entry;
  };

  const getParsedAmount = (amount) => (amount ? parseFloat(amount) : 0);

  asArray(products).forEach((product) => {
    if (product?.is_tax_exempt) {
      charges.tax_exempts.products.push({ product_id: product.id, reason: product.tax_exempt_reason });
    } else {
      asArray(product?.tax_ids)?.forEach((taxId) => {
        const tax = getProductTaxById(taxId);
        if (tax) {
          const taxesPercent = getParsedAmount(tax?.percentage);
          const taxesPercentAmount = formatCurrency(product.finalPrice * (taxesPercent / 100) || 0);
          const flatAmount = formatCurrency(getParsedAmount(tax?.amount));
          checkoutTaxes.push({
            ...tax,
            flatAmount,
            percentageAmount: taxesPercentAmount,
          });
          const chargeEntry = getOrCreateTaxCharge(tax);
          const taxChargeAmount = formatCurrency(flatAmount + taxesPercentAmount);
          chargeEntry.charged_products.push({ product_id: product.id, stylist_id: product.stylistId, product_price_excl_discount: product.finalPrice, tax_charge_amount: taxChargeAmount });
          chargeEntry.total_tax_amount += taxChargeAmount;
        }
      });
    }
    asArray(product?.fee_ids)?.forEach((feeId) => {
      const fee = getProductFeeById(feeId);
      if (fee) {
        const feesPercent = getParsedAmount(fee?.percentage);
        const feesPercentAmount = formatCurrency(product.finalPrice * (feesPercent / 100) || 0);
        const flatAmount = formatCurrency(getParsedAmount(fee?.amount));
        checkoutFees.push({
          ...fee,
          flatAmount,
          percentageAmount: feesPercentAmount,
        });
        const chargeEntry = getOrCreateFeeCharge(fee);
        const feeChargeAmount = formatCurrency(flatAmount + feesPercentAmount);
        const productStylistKey = String(product.stylistId ?? 'unassigned');
        if (!chargeEntry.stylists_with_fee[productStylistKey]) {
          chargeEntry.stylists_with_fee[productStylistKey] = { services_with_fee: [], products_with_fee: [] };
        }
        chargeEntry.stylists_with_fee[productStylistKey].products_with_fee.push(product.id);
        chargeEntry.fee_amount += feeChargeAmount;
        chargeEntry.fee_applied_to = fee.applied_to;
      }
    });
  });

  const checkoutTaxesAndFees = servicesInCheckout?.map((serviceInCheckout) => {
    const reCheckoutService = services.find((s) => s.id === serviceInCheckout.id);
    const isReCheckout = serviceInCheckout?.tax_ids === undefined && serviceInCheckout?.fee_ids === undefined;
    const service = {
      ...serviceInCheckout,
      tax_ids: isReCheckout ? reCheckoutService?.tax_ids ?? [] : serviceInCheckout?.tax_ids ?? [],
      fee_ids: isReCheckout ? reCheckoutService?.fee_ids ?? [] : serviceInCheckout?.fee_ids ?? [],
      is_tax_exempt: isReCheckout ? reCheckoutService?.is_tax_exempt ?? false : serviceInCheckout?.is_tax_exempt ?? false,
      tax_exempt_reason: isReCheckout ? reCheckoutService?.tax_exempt_reason ?? '' : serviceInCheckout?.tax_exempt_reason ?? '',
    };
    if (service?.is_tax_exempt) {
      charges.tax_exempts.services.push({ service_id: service.id, reason: service.tax_exempt_reason });
    } else {
      asArray(service?.tax_ids)?.forEach((taxId) => {
        const tax = getServiceTaxById(taxId);
        if (tax) {
          const taxesPercent = getParsedAmount(tax?.percentage);
          const taxesPercentAmount = formatCurrency(service.finalPrice * (taxesPercent / 100) || 0);
          const flatAmount = formatCurrency(getParsedAmount(tax?.amount));

          checkoutTaxes.push({
            ...tax,
            flatAmount,
            percentageAmount: taxesPercentAmount,
          });
          const chargeEntry = getOrCreateTaxCharge(tax);
          const taxChargeAmount = formatCurrency(flatAmount + taxesPercentAmount);
          chargeEntry.charged_services.push({ service_id: service.id, stylist_id: service.stylistId, service_price_excl_discount: service.finalPrice, tax_charge_amount: taxChargeAmount });
          chargeEntry.total_tax_amount += taxChargeAmount;
        }
      });
    }

    asArray(service?.fee_ids)?.forEach((feeId) => {
      const fee = getServiceFeeById(feeId);
      if (!fee) return;
      const feesPercent = getParsedAmount(fee?.percentage);
      const feesPercentAmount = formatCurrency(service.finalPrice * (feesPercent / 100) || 0);
      const flatAmount = formatCurrency(getParsedAmount(fee?.amount));
      checkoutFees.push({
        ...fee,
        flatAmount,
        percentageAmount: feesPercentAmount,
      });
      const chargeEntry = getOrCreateFeeCharge(fee);
      const feeChargeAmount = formatCurrency(flatAmount + feesPercentAmount);
      const serviceStylistKey = String(service.stylistId);
      if (!chargeEntry.stylists_with_fee[serviceStylistKey]) {
        chargeEntry.stylists_with_fee[serviceStylistKey] = { services_with_fee: [], products_with_fee: [] };
      }
      chargeEntry.stylists_with_fee[serviceStylistKey].services_with_fee.push(service.id);
      chargeEntry.fee_amount += feeChargeAmount;
      chargeEntry.fee_applied_to = fee.applied_to;
    });

    const taxesAmount = parseFloat((checkoutTaxes.reduce((acc, tax) => acc + (tax.flatAmount + tax.percentageAmount), 0) || 0).toFixed(2));
    const feesAmount = parseFloat((Array.from(checkoutFees).reduce((acc, fee) => acc + (fee.flatAmount + fee.percentageAmount), 0) || 0).toFixed(2));
    const balanceToPayInclTaxes = parseFloat((service.finalPrice + taxesAmount + feesAmount).toFixed(2));

    return {
      balanceToPayInclTaxes,
    };
  });

  charges.taxes.forEach((c) => {
    c.total_tax_amount = formatCurrency(c.total_tax_amount);
  });
  charges.fees.forEach((c) => {
    c.fee_amount = formatCurrency(c.fee_amount);
  });

  const totalTaxesAmount = checkoutTaxes.reduce((acc, tax) => acc + (tax.flatAmount + tax.percentageAmount), 0) || 0;
  const totalFeesAmount = checkoutFees.reduce((acc, fee) => acc + (fee.flatAmount + fee.percentageAmount), 0) || 0;

  const balanceToPayInclTaxes = checkoutTaxesAndFees?.reduce((acc, service) => acc + service?.balanceToPayInclTaxes, 0);

  return {
    taxes: checkoutTaxes,
    fees: checkoutFees,
    charges,
    balanceToPayInclTaxes: formatCurrency(balanceToPayInclTaxes),
    totalTaxesAmount: formatCurrency(totalTaxesAmount),
    totalFeesAmount: formatCurrency(totalFeesAmount),
    totalTaxesAndFeesAmount: formatCurrency(totalTaxesAmount + totalFeesAmount),
  };
};

const calculateFinalPrice = (item) => {
  let finalPrice;
  if (item.discountType === 'absolute') {
    finalPrice = item.price - item.discount;
  } else {
    const discount = item.price * (item.discount / 100);
    finalPrice = item.price - discount;
  }
  return finalPrice ? parseFloat(finalPrice.toFixed(2)) : 0;
};

const calculateProductFinalPrice = (item) => {
  let finalPrice;
  if (item.discountType === 'absolute') {
    finalPrice = item.price * item.quantity - item.discount;
  } else {
    const discount = item.price * (item.discount / 100);
    finalPrice = (item.price - discount) * item.quantity;
  }
  return finalPrice ? parseFloat(finalPrice.toFixed(2)) : 0;
};

const checkoutReducer = (state = initState(), action) => {
  const { type: actionType } = action ?? {};

  const replacementReducer = MAP[action?.type];
  if (isFn(replacementReducer)) {
    L.debug('()', 'Using replacement reducer for actionType:', actionType);
    // this is a new code with new actions, so use new reducer for it as to not disturb the old one
    return replacementReducer(state ?? {}, action ?? {});
  } else if (replacementReducer) {
    L.warn('()', 'Invalid function replacementReducer:', replacementReducer);
  }

  if (!actionType) return state;

  switch (actionType) {
    case SET_CHECKOUT_DATA:
      return { ...state, data: action.data };
    case feCleanCheckoutData.AT.name:
      return { ...initState() };

    case aTypeInitCheckoutData.name: {
      const aId = action?.initData?.appointment?.id;
      const cId = action?.initData?.appointment?.client?.id;

      L.debug('()', aTypeInitCheckoutData.name, { aId, cId }, action, state);

      const addedServices = [];
      const appointmentIds = [];
      const registeredServices = action?.registeredServices;

      if (cId) {
        for (const procedure of asArray(action?.allProceduresForTheDay)) {
          if (cId === procedure?.appointment?.client?.id && AP_STATUS.paid !== procedure?.appointment?.status) {
            const appointmentId = procedure?.appointment?.id;

            const registeredService = registeredServices.find((s) => s.id === procedure?.service?.id);
            addedServices.push({
              ...(procedure?.service ?? {}),
              tax_ids: registeredService?.tax_ids ?? [],
              fee_ids: registeredService?.fee_ids ?? [],
              is_tax_exempt: registeredService?.is_tax_exempt ?? false,
              tax_exempt_reason: registeredService?.tax_exempt_reason ?? '',
              price: procedure.price,
              stylist_id: procedure.stylist?.id,
              start_time: procedure.start_time,
              appointmentId,
            });

            if (!appointmentIds.includes(appointmentId)) appointmentIds.push(appointmentId);
          }
        } // for
      } else {
        for (const procedure of asArray(action?.proceduresInAppointment)) {
          const registeredService = registeredServices.find((s) => s.id === procedure?.service?.id);
          addedServices.push({
            ...(procedure?.service ?? {}),
            tax_ids: registeredService?.tax_ids ?? [],
            fee_ids: registeredService?.fee_ids ?? [],
            is_tax_exempt: registeredService?.is_tax_exempt ?? false,
            tax_exempt_reason: registeredService?.tax_exempt_reason ?? '',
            price: procedure.price,
            stylist_id: procedure.stylist?.id,
            start_time: procedure.start_time,
            finalPrice: parseFloat(procedure.price.toFixed(2)),
          });
          appointmentIds.push(aId);
        }
      }

      const data = {
        ...initState().data,
        effective_date: action.initData.start_time,
        active_card: state.data ? state.data.active_card : null,
        clients: [
          {
            id: cId || 0,
            clientId: cId || 0,
            appointmentId: aId,
            services: asArray(addedServices).map((service) => {
              const registeredService = registeredServices.find((s) => s.id === service.id);
              return {
                id: service.id,
                appointmentId: service.appointmentId,
                discount: 0,
                discountType: 'absolute',
                price: service.price,
                finalPrice: parseFloat(service.price.toFixed(2)),
                stylistId: service.stylist_id,
                start_time: service.start_time,
                tax_ids: registeredService?.tax_ids ?? [],
                fee_ids: registeredService?.fee_ids ?? [],
                is_tax_exempt: registeredService?.is_tax_exempt ?? false,
                tax_exempt_reason: registeredService?.tax_exempt_reason ?? '',
              };
            }),
            products: [],
            nextAppointment: null,
          },
        ],
      };

      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);

      return {
        ...state,
        checkoutLoaded: true,
        data,
        bookingData: action?.initData,
        clientId: cId ?? null,
        appointments: appointmentIds,
        finishUrl: action.finishUrl,
        allProceduresForTheDay: action.allProceduresForTheDay,
      };
    }

    case SET_CHECKOUT_INIT_DATA_FROM_MAIN_NAV: {
      return {
        ...state,
        data: {
          ...initState().data,
          clients: [
            {
              id: null,
              services: [],
              products: [],
            },
          ],
        },
        bookingData: { appointment: { salonId: action?.salonId } },
      };
    }
    case feSetCheckoutInitDataFromClientProfile.AT.name: {
      const { clientId, salonId } = asObject(action?.data);
      return {
        ...state,
        data: {
          ...initState().data,
          clients: [
            {
              id: clientId,
              clientId,
              services: [],
              products: [],
            },
          ],
        },
        bookingData: { appointment: { salonId } },
        clientId,
      };
    }
    case SET_CHECKOUT_INIT_DATA_FROM_CASHUP: {
      const { data, salonId } = action;
      const { data: stateData } = state;
      data.created_from = action.data.created_from;
      data.fromCashUp = true;
      data.cardAmount = 0;
      data.onlineAmount = 0;
      data.partCash = 0;
      data.otherAmount = action.data.otherAmount;
      let refreshTotal = 0;
      action.data.clients.forEach((client) => {
        client.services.forEach((service) => {
          refreshTotal += service.finalPrice;
        });
        client.products.forEach((product) => {
          refreshTotal += product.finalPrice;
        });
      });
      refreshTotal -= action.data.otherAmount;
      const depositData = data.deposits[0];
      if (data.deposit && stateData.deposits) {
        const depositAppoitments = depositData.used_for_appointmentIds;

        refreshTotal -= Math.abs(depositData.amount);
        const { clientId } = depositData;
        if (data.deposit.used_for_appointmentIds === null) delete data.deposit.used_for_appointmentIds;
        if (data.deposit.created_for_appointmentId === null) delete data.deposit.created_for_appointmentId;
        return {
          ...state,
          appointments: depositAppoitments,
          clientId,
          data: {
            ...stateData,
            ...data,
            deposits: {
              ...stateData.deposits,
            },
            deposit: {
              ...data.deposit,
              amount: Math.abs(data.deposit.amount),
            },
            total: refreshTotal,
            balanceToPay: refreshTotal,
          },
          initialDeposit: {
            response: {
              id: data.deposit.id,
              clientId: data.deposit.clientId,
              amount: Math.abs(data.deposit.amount),
              depositType: 'deposited',
              // created_at: ____,
              notes: 'used in checkout',
              /* used_for_appointmentId: ______,
          created_for_appointmentId: ____, */
            },
            isFetching: false,
            error: null,
          },
          bookingData: { appointment: { salonId } },
          finishUrl: action.finishUrl,
        };
      }
      const selectApp = { id: data.clients[0].appointmentId, client_id: data.clients[0].id };
      const currentAppIds = data.clients.filter((c) => c.appointmentId).map((x) => x.appointmentId);
      return {
        ...state,
        appointments: currentAppIds,
        clientId: data.clients[0].id,
        data: { ...stateData, ...data },
        selectedAppointment: selectApp,
        initialAppointment: selectApp,
        bookingData: { appointment: { salonId } },
        finishUrl: action.finishUrl,
      };
    }
    case ADD_CLIENT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      const addedAppointments = [...state.appointments];
      const addedServices = [];
      let willAdd = true;
      const clientID = action.client.id;
      for (let i = 0; i < clients.length; i += 1) {
        if (clientID && clientID === clients[i].id) {
          willAdd = false;
          break;
        }
      }
      if (willAdd) {
        const clientDayProcedures = action.allProceduresForTheDay.filter((c) => (c.appointment && c.appointment.client ? c.appointment.client.id === clientID : false));
        for (let i = 0; i < clientDayProcedures.length; i += 1) {
          if (clientDayProcedures[i].appointment.status !== 'paid') {
            const service = {
              ...clientDayProcedures[i].service,
              price: clientDayProcedures[i].price,
              stylist_id: clientDayProcedures[i].stylist.id,
              start_time: clientDayProcedures[i].start_time,
              appointmentId: clientDayProcedures[i].appointment.id,
            };
            addedServices.push(service);
            const appointmentId = clientDayProcedures[i].appointment.id;
            if (addedAppointments.indexOf(appointmentId) === -1) {
              addedAppointments.push(clientDayProcedures[i].appointment.id);
            }
          }
        }

        const clientServices = [];
        for (let i = 0; i < addedServices.length; i += 1) {
          clientServices.push({
            id: addedServices[i].id,
            appointmentId: addedServices[i].appointmentId,
            discount: 0,
            discountType: 'absolute',
            price: addedServices[i].price,
            finalPrice: parseFloat(addedServices[i].price.toFixed(2)),
            stylistId: addedServices[i].stylist_id,
            start_time: addedServices[i].start_time,
            tax_ids: addedServices[i].tax_ids,
            fee_ids: addedServices[i].fee_ids,
            is_tax_exempt: addedServices[i].is_tax_exempt ?? false,
            tax_exempt_reason: addedServices[i].tax_exempt_reason ?? '',
          });
        }

        const addedClient = action.client;
        addedClient.services = clientServices;
        clients.push(action.client);
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
        appointments: addedAppointments,
      };
    }
    case REMOVE_CLIENT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      let filterApp = [...state.appointments];
      const currentDepositList = [...state.data.depositListData];
      const updateDepositList = [];
      if (clients[action.index]) {
        const removeClientId = clients[action.index].id;
        const clientDayProcedures = state.allProceduresForTheDay.filter((c) => (c.appointment && c.appointment.client ? c.appointment.client.id === removeClientId : false));
        if (clientDayProcedures.length > 0) {
          clientDayProcedures.forEach((procedure) => {
            filterApp = filterApp.filter((c) => c !== procedure.appointment.id);
            return true;
          });
        }
        currentDepositList.forEach((d) => {
          if (d.mainClientId !== removeClientId) {
            updateDepositList.push(d);
          }
        });
        clients.splice(action.index, 1);
        data.depositListData = updateDepositList;
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
        appointments: filterApp,
      };
    }
    case ADD_SERVICE: {
      const data = { ...(state.data ?? {}) };
      const clients = [...data.clients];
      const service = { ...action.service };
      data.depositListData = [...(data.depositListData ?? {})];
      service.price = priceForStylist(service, service.stylistId, action.services, action.stylists);
      service.finalPrice = calculateFinalPrice(service);
      if (clients[action.clientIndex]) {
        clients[action.clientIndex].services.push(service);
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return { ...state, data };
    }
    case REMOVE_SERVICE: {
      const { appointments } = state;
      // const index = appointments.indexOf(action.appointmentId);
      // appointments.splice(index, 1);
      const data = Object.assign({}, state.data);
      const clients = data.clients.slice();
      if (clients[action.clientIndex] && clients[action.clientIndex].services[action.serviceIndex]) {
        const services = clients[action.clientIndex].services.slice();
        services.splice(action.serviceIndex, 1);
        clients[action.clientIndex].services = services;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (newTotal === 0) {
            const { initialDeposit } = state;
            const newDepositListData = [];
            if (initialDeposit && initialDeposit.response) {
              for (let i = 0; i < data.depositListData.length; i += 1) {
                if (data.depositListData[i].used_for_appointmentIds[0] === initialDeposit.response.created_for_appointmentId) {
                  newDepositListData.push(data.depositListData[i]);
                }
              }
            }
            data.depositListData = newDepositListData;
          } else if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
        appointments,
      };
    }
    case UPDATE_SERVICE_STYLIST_ID: {
      const data = Object.assign({}, state.data);
      const clients = data.clients.slice();
      if (clients[action.clientIndex] && clients[action.clientIndex].services[action.serviceIndex]) {
        const services = clients[action.clientIndex].services.slice();
        const service = Object.assign({}, services[action.serviceIndex]);
        service.stylistId = action.stylistId;
        service.price = priceForStylist(service, action.stylistId, action.categoryServices, action.stylists);
        service.finalPrice = calculateFinalPrice(service);
        services[action.serviceIndex] = service;
        clients[action.clientIndex].services = services;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = data.depositListData.slice();
            newUseDepositList.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                item.use_deposit_string = item.use_deposit.toString();
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_SERVICE_PRICE: {
      const data = Object.assign({}, state.data);
      // let setList = state.data.depositListData;
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].services[action.serviceIndex]) {
        const services = clients[action.clientIndex].services.slice();
        const service = Object.assign({}, services[action.serviceIndex]);
        service.price = action.price;
        service.finalPrice = calculateFinalPrice(service);
        services[action.serviceIndex] = service;
        clients[action.clientIndex].services = services;
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_SERVICE_DISCOUNT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].services[action.serviceIndex]) {
        const services = clients[action.clientIndex].services.slice();
        const service = Object.assign({}, services[action.serviceIndex]);
        service.discount = action.discount;
        const calServiceFinalPrice = calculateFinalPrice(service);
        service.finalPrice = parseFloat(calServiceFinalPrice.toFixed(2));
        services[action.serviceIndex] = service;
        clients[action.clientIndex].services = services;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case SET_CHECKOUT_SALON_TAXES_AND_FEES:
      return { ...state, ...{ data: { ...state.data, salonTaxesAndFees: { ...action.payload } } } };
    case CHANGE_SERVICE_DISCOUNT_TYPE: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].services[action.serviceIndex]) {
        const services = clients[action.clientIndex].services.slice();
        const service = Object.assign({}, services[action.serviceIndex]);
        service.discountType = action.discountType;
        service.finalPrice = calculateFinalPrice(service);
        services[action.serviceIndex] = service;
        clients[action.clientIndex].services = services;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case CHECKOUT_START_SESSION_SUCCESS: {
      const { defaultStylistId } = action.params;
      return {
        ...state,
        defaultStylistId,
      };
    }
    case EXPORT_TRANSACTION_REQUEST: {
      return {
        ...state,
        isFetching: true,
        transactionLog: null,
      };
    }
    case EXPORT_TRANSACTION_SUCCESS: {
      return {
        ...state,
        isFetching: false,
        transactionLog: getAny(0, 'response.length', action) > 100 ? action.response : null,
      };
    }
    case CLEAR_TRANSACTION_LOG: {
      return {
        ...state,
        transactionLog: null,
      };
    }
    case EXPORT_TRANSACTION_FAILURE: {
      return {
        ...state,
        isFetching: false,
        errorMessage: action.errorMessage,
      };
    }
    case CHECKOUT_ADD_PRODUCT_FROM_SCAN: {
      if (action.data.action === 'sale') {
        const { data } = state;
        const clients = [...data.clients];
        const lastClient = data.clients.length - 1;
        if (clients[lastClient]) {
          const {
            data: {
              data: { data: product },
            },
          } = action;
          product.price = parseFloat(product.price);
          product.finalPrice = product.price ? parseFloat(product.price.toFixed(2)) : 0;
          product.discount = 0;
          product.quantity = 1;
          product.discountType = 'absolute';
          product.quantity = 1;
          let stylistId = 0;
          if (clients[lastClient].products.length > 0) {
            // eslint-disable-next-line
            stylistId = clients[lastClient].products[clients[lastClient].products.length - 1].stylistId;
          } else if (clients[lastClient].services.length > 0) {
            // eslint-disable-next-line
            stylistId = clients[lastClient].services[clients[lastClient].services.length - 1].stylistId;
          }
          if (stylistId === 0) {
            stylistId = state.defaultStylistId;
          }
          product.stylistId = stylistId;
          clients[lastClient].products.push(product);
          data.clients = clients;
          const newTotal = calculateTotal(data);
          data.total = newTotal;
          if (data.depositListData.length > 0) {
            let depositTotal = 0;
            data.depositListData.map((d) => {
              depositTotal += d.use_deposit;
              return true;
            });
            if (depositTotal > newTotal) {
              // update the use_deposit list and the deposit list
              let downGradeValue = depositTotal - newTotal;
              const newUseDepositList = [];
              data.depositListData.forEach((d) => {
                if (d.use_deposit > downGradeValue) {
                  const item = d;
                  item.use_deposit -= downGradeValue;
                  newUseDepositList.push(item);
                  downGradeValue = 0;
                } else {
                  downGradeValue -= d.use_deposit;
                }
              });
              data.depositListData = newUseDepositList;
            }
          }
          data.balanceToPay = calculateBalance(data);
        }
        return {
          ...state,
          data,
        };
      }
      return { ...state };
    }
    case ADD_PRODUCT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex]) {
        const editProduct = action.product;
        if (!action.product.price) {
          editProduct.price = 0;
        }
        clients[action.clientIndex].products.push(editProduct);
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return { ...state, data };
    }
    case REMOVE_PRODUCT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        products.splice(action.productIndex, 1);
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_PRODUCT_STYLIST_ID: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.stylistId = action.stylistId;
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_PRODUCT_TITLE: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.title = action.title;
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        data.total = calculateTotal(data);
        data.balanceToPay = calculateBalance(data);
      }
      return { ...state, data };
    }
    case UPDATE_PRODUCT_PRICE: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.price = action.price;
        product.finalPrice = calculateProductFinalPrice(product);
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_PRODUCT_DISCOUNT: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.discount = action.discount;
        product.finalPrice = calculateProductFinalPrice(product);
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_PRODUCT_QUANTITY: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.quantity = action.quantity;
        product.finalPrice = calculateProductFinalPrice(product);
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case CHANGE_PRODUCT_DISCOUNT_TYPE: {
      const data = Object.assign({}, state.data);
      const clients = [...data.clients];
      if (clients[action.clientIndex] && clients[action.clientIndex].products[action.productIndex]) {
        const products = clients[action.clientIndex].products.slice();
        const product = Object.assign({}, products[action.productIndex]);
        product.discountType = action.discountType;
        product.finalPrice = calculateProductFinalPrice(product);
        products[action.productIndex] = product;
        clients[action.clientIndex].products = products;
        data.clients = clients;
        const newTotal = calculateTotal(data);
        data.total = newTotal;
        if (data.depositListData.length > 0) {
          let depositTotal = 0;
          data.depositListData.map((d) => {
            depositTotal += d.use_deposit;
            return true;
          });
          if (depositTotal > newTotal) {
            // update the use_deposit list and the deposit list
            let downGradeValue = depositTotal - newTotal;
            const newUseDepositList = [];
            data.depositListData.forEach((d) => {
              if (d.use_deposit > downGradeValue) {
                const item = d;
                item.use_deposit -= downGradeValue;
                newUseDepositList.push(item);
                downGradeValue = 0;
              } else {
                downGradeValue -= d.use_deposit;
              }
            });
            data.depositListData = newUseDepositList;
          }
        }
        data.balanceToPay = calculateBalance(data);
      }
      return {
        ...state,
        data,
      };
    }
    case UPDATE_TIP_AMOUNT: {
      const data = Object.assign({}, state.data);
      data.tipAmount = action.tipAmount;
      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case CHANGE_TIP_TYPE: {
      const data = Object.assign({}, state.data);
      data.tipType = action.tipType;
      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case UPDATE_ADDITIONAL_DISCOUNT: {
      const data = Object.assign({}, state.data);
      data.additionalDiscount = action.additionalDiscount;
      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case CHANGE_ADDITIONAL_DISCOUNT_TYPE: {
      const data = Object.assign({}, state.data);
      data.additionalDiscountType = action.additionalDiscountType;
      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case UPDATE_ADDITIONAL_DISCOUNT_REASON: {
      const data = Object.assign({}, state.data);
      data.additionalDiscountReason = action.additionalDiscountReason;
      return { ...state, data };
    }
    case UPDATE_PART_CASH: {
      const data = Object.assign({}, state.data);
      data.partCash = action.partCash;
      data.total = calculateTotal(data);
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }

    case UPDATE_DEPOSIT_FROM_CHECKOUT: {
      const data = Object.assign({}, state.data);
      const setUpdatedList = [];
      const selectedDeposit = action.response;

      for (const deposit of data.depositListData) {
        if (deposit.fe_Id === selectedDeposit.fe_Id) {
          if (true === selectedDeposit.selected) {
            deposit.use_deposit = selectedDeposit.old_use_deposit || selectedDeposit.use_deposit;
            deposit.use_deposit_string = selectedDeposit.use_deposit_string ? selectedDeposit.use_deposit_string : '';
            deposit.selected = true;
            deposit.depositType = 'used';
          } else {
            // save the use_deposit before set to 0
            deposit.old_use_deposit = selectedDeposit.old_use_deposit;
            deposit.selected = false;
            deposit.use_deposit = 0;
            deposit.use_deposit_string = '';
          }
        } // if-else
        setUpdatedList.push(deposit);
      } // for

      data.depositListData = setUpdatedList;
      data.balanceToPay = calculateBalance(data);

      return { ...state, data };
    }

    case UPDATE_OTHER_AMOUNT: {
      const data = Object.assign({}, state.data);
      data.otherAmount = action.otherAmount;
      // data.total = calculateTotal(data);
      return { ...state, data };
    }
    case UPDATE_VOUCHERS_USED: {
      const data = Object.assign({}, state.data);
      data.vouchers_used = action.vouchers_used;
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case CLEAN_TRANSACTION_DATA: {
      return {
        ...state,
        data: {
          ...state.data,
          vouchers_used: [],
        },
      };
    }
    case CHANGE_PAYMENT_TYPE: {
      return {
        ...state,
        data: {
          ...state.data,
          paymentType: action.paymentType,
        },
      };
    }
    case SET_CHECKOUT_TOTAL: {
      const data = Object.assign({}, state.data);
      data.total = action.total;
      data.balanceToPay = calculateBalance(data);
      return { ...state, data };
    }
    case APPOINTMENT_REQUEST: {
      // if used in activity feed
      if (window.location.href.indexOf('feed') > 0) {
        return {
          ...state,
        };
      }
      return { ...state, isFetching: true };
    }

    /* if checkout 2 appointments at the same time
  we need to get all procedures for all appointment
  called by SendReceipt to get the updated price of
  procedures if price changed during checkout
  initialAppointment won't change after adding other procedures when checkout
  */
    case APPOINTMENT_SUCCESS: {
      // if used in activity feed
      if (window.location.href.indexOf('feed') > 0) return { ...state };

      const responseClient = action?.response?.client;
      const responseProcedures = action?.response?.procedures;

      // FIXME: shared mutable state !!!
      // NOTE: shared mutable state was used in the past to modify the BE response
      // the idea was to combine multiple appointments of a single client
      // but that shouldn't happen for walk-in appointments that have null client
      const procedures = responseClient ? asArray(responseProcedures) : [...asArray(responseProcedures)];
      const existingProcedureIds = procedures.map((p) => p.id);

      for (const procedure of asArray(state?.selectedAppointment?.procedures)) {
        if (!existingProcedureIds.includes(procedure.id)) {
          // FIXME: shared mutable state !!! this modifies the original BE response before components can use it
          procedures.push(procedure);
        }
      }

      return {
        ...state,
        isFetching: false,
        selectedAppointment: {
          ...(action.response ?? state.selectedAppointment ?? {}),
          procedures,
        },
        initialAppointment: state.initialAppointment || state.selectedAppointment,
        data: {
          ...(state?.data ?? {}),
          active_card: responseClient?.active_card ?? state?.data?.active_card,
        },
      };
    }

    case APPOINTMENT_FAILURE: {
      if (window.location.href.indexOf('feed') > 0) {
        return {
          ...state,
        };
      }
      return { ...state, isFetching: false, errorMessage: action.errorMessage };
    }
    case TRANSACTION_DETAILS_REQUEST:
      return { ...state, isFetching: true };
    case TRANSACTION_DETAILS_SUCCESS: {
      const selectedTransaction = action.response;
      // re-checkout shouldn't have vouchers_used
      selectedTransaction.vouchers_used = null;
      return { ...state, isFetching: false, selectedTransaction };
    }
    case TRANSACTION_DETAILS_FAILURE:
      return { ...state, isFetching: false, errorMessage: action.errorMessage };
    case CHECKOUT_CLIENT_REQUEST:
      return {
        ...state,
        edit_client: {
          ...state.edit_client,
          isFetching: true,
        },
      };
    case UNLOCK_DEPOSIT_REQUEST: {
      const newDepositUnlock = { ...state.depositUnlock, [action.params.appointmentId]: '' };
      return {
        ...state,
        isFetching: true,
        depositUnlock: newDepositUnlock,
      };
    }
    case UNLOCK_DEPOSIT_SUCCESS: {
      const newDepositUnlock = { ...state.depositUnlock, [action.params.appointmentId]: 'Success' };
      return {
        ...state,
        isFetching: false,
        depositUnlock: newDepositUnlock,
      };
    }
    case UNLOCK_DEPOSIT_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.error,
        depositUnlock: { [action.params.appointmentId]: 'Failed' },
      };
    case CHECKOUT_CLIENT_SUCCESS:
      return {
        ...state,
        edit_client: {
          ...state.edit_client,
          isFetching: false,
          data: action.response,
        },
      };
    case CHECKOUT_CLIENT_FAILURE:
      return {
        ...state,
        edit_client: {
          ...state.edit_client,
          isFetching: false,
          error: action.error,
        },
      };
    case ADD_CLIENT_DEPOSITS: {
      const data = {
        // ...state, // guan commented out on 29 May 2021
        ...state.data,
        deposits: {
          ...state.data.deposits,
        },
      };
      data.total = calculateTotal(data);
      return {
        ...state,
        data,
      };
    }
    case ADD_CLIENT_SEARCH_DEPOSITS: {
      const dataChange = {
        ...state.data,
      };
      const setListWithDeposit = [...state.data.depositListData];
      const clientDeposit = action.client;
      if (clientDeposit) {
        if (clientDeposit.card !== 0) {
          setListWithDeposit.push({
            available: clientDeposit.card_available,
            locked: clientDeposit.card_locked,
            total: clientDeposit.card,
            source: 'card',
            selected: clientDeposit.card_available > 0,
            fe_Id: setListWithDeposit.length + 1,
            mainClientId: clientDeposit.clientId,
            clientId: clientDeposit.clientId,
            client_name: clientDeposit.user_name,
            used_for_appointmentIds: state.selectedAppointment ? [state.selectedAppointment.id] : [],
          });
        }
        if (clientDeposit.cash !== 0) {
          setListWithDeposit.push({
            available: clientDeposit.cash_available,
            locked: clientDeposit.cash_locked,
            total: clientDeposit.cash,
            source: 'cash',
            selected: clientDeposit.cash_available > 0,
            fe_Id: setListWithDeposit.length + 1,
            mainClientId: clientDeposit.clientId,
            clientId: clientDeposit.clientId,
            client_name: clientDeposit.user_name,
            used_for_appointmentIds: state.selectedAppointment ? [state.selectedAppointment.id] : [],
          });

          // dataChange.balanceToPay -= clientDeposit.cash_available;
        }

        if (clientDeposit.online !== 0) {
          setListWithDeposit.push({
            available: clientDeposit.online_available,
            locked: clientDeposit.online_locked,
            total: clientDeposit.online,
            source: 'online',
            selected: clientDeposit.online_available > 0,
            fe_Id: setListWithDeposit.length + 1,
            mainClientId: clientDeposit.clientId,
            clientId: clientDeposit.clientId,
            client_name: clientDeposit.user_name,
            used_for_appointmentIds: state.selectedAppointment ? [state.selectedAppointment.id] : [],
          });
        }
        // guan added
        const depositWithLocked = setListWithDeposit.filter((e) => e.clientId === clientDeposit.clientId && e.locked !== 0);
        if (depositWithLocked.length) {
          for (let i = 0; i < setListWithDeposit.length; i += 1) {
            if (setListWithDeposit[i].locked === clientDeposit.cash_locked) {
              if (!setListWithDeposit[i].locked_on_appointments) {
                setListWithDeposit[i].locked_on_appointments = clientDeposit.cash_locked_on_appointments.slice();
              } else {
                setListWithDeposit[i].locked_on_appointments.push(clientDeposit.cash_locked_on_appointments.slice());
              }
            }
            if (setListWithDeposit[i].locked === clientDeposit.card_locked) {
              if (!setListWithDeposit[i].locked_on_appointments) {
                setListWithDeposit[i].locked_on_appointments = clientDeposit.card_locked_on_appointments.slice();
              } else {
                setListWithDeposit[i].locked_on_appointments.push(clientDeposit.card_locked_on_appointments.slice());
              }
            }
            if (setListWithDeposit[i].locked === clientDeposit.online_locked) {
              if (!setListWithDeposit[i].locked_on_appointments) {
                setListWithDeposit[i].locked_on_appointments = clientDeposit.online_locked_on_appointments.slice();
              } else {
                setListWithDeposit[i].locked_on_appointments.push(clientDeposit.online_locked_on_appointments.slice());
              }
            }
          }
        }
        // end of guan added
      }
      dataChange.depositListData = setListWithDeposit;
      return {
        ...state,
        data: dataChange,
      };
    }
    case CHECKOUT_FETCH_DEPOSIT_REQUEST:
      return {
        ...state,
        initialDeposit: {
          response: null,
          isFetching: true,
          error: null,
        },
      };
    case CHECKOUT_INIT_DEPOSIT_REQUEST:
      return {
        ...state,
      };
    case CHECKOUT_INIT_DEPOSIT_SUCCESS: {
      return reduceInitDepositSuccess(state, action);
    }
    case CHANGE_APPOINTMENT_STATUS_SUCCESS: {
      const { depositCreatedForCheckout } = state;
      const changeStatus = action.response[0];
      if (changeStatus.action === DEPOSIT_ACTION.refund) {
        // that deposit is not available now
        delete depositCreatedForCheckout[changeStatus.id];
      }
      return {
        ...state,
        depositCreatedForCheckout,
      };
    }
    case CHECKOUT_FETCH_DEPOSIT_SUCCESS:
      return {
        ...state,
        initialDeposit: {
          response: action.response,
          isFetching: false,
          error: null,
        },
        data: {
          ...state.data,
          deposit: {
            amount: action.response.amount,
            clientId: action.response.clientId,
            depositType: 'used',
            notes: 'used in checkout',
          },
        },
      };
    case CHECKOUT_FETCH_DEPOSIT_FAILURE:
      return {
        ...state,
        initialDeposit: {
          response: null,
          isFetching: false,
          error: action.error.errorMessage,
        },
      };
    case TOGGLE_CHECKOUT_BUTTONS_ENABLED:
      return {
        ...state,
        checkoutPayButtonsDisabled: action.enabled,
      };
    case DEPOSIT_CLIENT_LIST_REQUEST: {
      return {
        ...state,
        depositClientListSuccess: false,
      };
    }
    case DEPOSIT_CLIENT_LIST_SUCCESS: {
      const initDeposit = state.initialDeposit.response;
      const setList = [...state.data.depositListData];
      const dataChange = {
        ...state.data,
      };
      const salonID = action.params.salonId;
      const totalToPay = state.data.total;
      const clientID = action.params.clientId;
      let unlockedDeposit = false;
      let appointmentIdUnlocked;
      if (state.depositUnlock && Object.values(state.depositUnlock).includes('Success')) {
        unlockedDeposit = true;
        appointmentIdUnlocked = Object.entries(state.depositUnlock)
          .filter((e) => e[1] === 'Success')
          .map((d) => parseInt(d[0], 10));
      }
      // from state.depositUnlock update the available , locked and locked_on_appointments
      if (unlockedDeposit && appointmentIdUnlocked.length) {
        const oldList = state.data.depositListData;
        for (let n = 0; n < appointmentIdUnlocked.length; n += 1) {
          for (let i = 0; i < oldList.length; i += 1) {
            if (oldList[i].locked_on_appointments.length) {
              for (let j = 0; j < oldList[i].locked_on_appointments.length; j += 1) {
                if (oldList[i].locked_on_appointments[j].appointment === appointmentIdUnlocked[n]) {
                  oldList[i].available += oldList[i].locked_on_appointments[j].locked;
                  oldList[i].locked -= oldList[i].locked_on_appointments[j].locked;
                  oldList[i].locked_on_appointments.splice(j, 1);
                  break;
                }
              }
            }
          }
        }
        // now we have used state.depositUnlock, need to clear it for next unlock
        dataChange.depositListData = oldList;
      }
      if (state.appointments.length < 2 && initDeposit) {
        if (setList.filter((c) => c.mainClientId === clientID).length === 0) {
          action.response.map((p, index) =>
            setList.push({
              ...p,
              selected: p.source === initDeposit.source && p.clientId === initDeposit.clientId,
              fe_Id: index + 1,
              mainClientId: clientID,
              depositType: 'used',
              used_for_appointmentIds: state.selectedAppointment ? [state.selectedAppointment.id] : [],
              salonId: state.selectedAppointment ? state.selectedAppointment.salon_id : salonID,
              use_deposit:
                // eslint-disable-next-line no-nested-ternary
                p.source === initDeposit.source && p.clientId === initDeposit.clientId ? (totalToPay < initDeposit.amount ? totalToPay : initDeposit.amount) : 0,
              use_deposit_string:
                // eslint-disable-next-line no-nested-ternary
                p.source === initDeposit.source && p.clientId === initDeposit.clientId ? (totalToPay < initDeposit.amount ? totalToPay.toFixed(2) : initDeposit.amount.toFixed(2)) : '',
            }),
          );

          dataChange.balanceToPay -= totalToPay < initDeposit.amount ? totalToPay : initDeposit.amount;
        }
      } else if (setList.filter((c) => c.mainClientId === clientID).length === 0) {
        const calcIndex = setList.length;
        action.response.map((p, index) => {
          if (setList.filter((c) => c.clientId === p.clientId && c.source === p.source).length === 0) {
            setList.push({
              ...p,
              selected: false,
              mainClientId: clientID,
              fe_Id: index + calcIndex + 1,
              depositType: 'used',
              used_for_appointmentIds: state.selectedAppointment ? [state.selectedAppointment.id] : [],
              salonId: state.selectedAppointment ? state.selectedAppointment.salon_id : salonID,
              use_deposit: 0,
              use_deposit_string: '',
            });
          }
          return true;
        });
      }
      dataChange.depositListData = setList;
      if (dataChange.fromCashUp) {
        const newSetList = [];
        setList.forEach((item) => {
          const addItem = item;

          const depositMatch = dataChange.deposits.filter((c) => c.source === item.source && c.clientId === item.clientId);
          if (depositMatch.length > 0) {
            addItem.selected = true;
            const useDeposit = Math.abs(depositMatch[0].amount);
            addItem.use_deposit = useDeposit;
            addItem.use_deposit_string = useDeposit.toFixed(2);
          }

          newSetList.push(addItem);
          return true;
        });
        dataChange.depositListData = newSetList;
        dataChange.balanceToPay = calculateBalance(dataChange);
      }
      return {
        ...state,
        data: dataChange,
        depositUnlock: null,
        depositClientListSuccess: true,
      };
    }
    case CHARGE_CARD_ONFILE_REQUEST:
      return { ...state, isChargingCard: action.isFetching, cardOnFileChargeSuccess: false };
    case CHARGE_CARD_ONFILE_SUCCESS: {
      const chargingCardError = action.response.error === 'authentication_required' ? 'authentication_required' : null;
      const cardOnFileChargeSuccess = chargingCardError ? false : action.response.success;

      return { ...state, isChargingCard: true, cardOnFileChargeSuccess, chargingCardError };
    }
    case CANCEL_SLICK_CARD_ONLINE: {
      const dataChange = {
        ...state.data,
      };
      dataChange.onlineAmount = 0;
      dataChange.cardAmount = 0;
      return {
        ...state,
        data: dataChange,
      };
    }
    case CHARGE_CARD_ONFILE_FAILURE: {
      return {
        ...state,
        isChargingCard: false,
        cardOnFileChargeSuccess: false,
        chargingCardError: action.error,
      };
    }

    case UPDATE_CHECKOUT_COMPLETE: {
      const { cashValue, otherValue, chargecardOnFile, isClientCard, balanceToPay } = action;
      const { active_card: activeCard } = state.data;

      let cardSum = balanceToPay - cashValue - otherValue;
      // TODO: is cardSum <= 0 && otherValue === 0 the same as cashValue > balanceToPay? check and remove if so
      let cashAmount = cardSum <= 0 && otherValue === 0 && cashValue > balanceToPay ? balanceToPay : cashValue;

      if (otherValue > 0) {
        if (cardSum + cashValue + otherValue > balanceToPay) {
          cashAmount = balanceToPay - otherValue - cardSum;
        }
        if (cashValue + otherValue > balanceToPay) {
          cashAmount = balanceToPay - otherValue;
        }
        if (cardSum + otherValue > balanceToPay) {
          cardSum = balanceToPay - otherValue;
        }
      }

      const data = {
        ...state.data,
        otherAmount: parseFloat(otherValue.toFixed(2)),
        partCash: parseFloat(cashAmount.toFixed(2)),
      };

      if (cardSum > 0) {
        const isOnline = fromAnyToBool(isClientCard && activeCard && chargecardOnFile);
        const cardSumParsed = parseFloat(cardSum.toFixed(2));

        data.onlineAmount = isOnline ? cardSumParsed : 0;
        data.cardAmount = isOnline ? 0 : cardSumParsed;
      }

      return { ...state, data, chargingCardError: null };
    }

    case CHECKOUT_FETCH_ALL_DEPOSITS_REQUEST:
      return {
        ...state,
      };
    case CHECKOUT_FETCH_ALL_DEPOSITS_SUCCESS: {
      return {
        ...state,
        clientsDeposits: action.response,
      };
    }
    case CHECKOUT_FETCH_ALL_DEPOSITS_FAILURE:
      return {
        ...state,
      };
    case FETCH_NEXT_APPOINTMENT_SUCCESS: {
      const newAppointment = action.response;
      const dataChange = {
        ...state.data,
      };
      const apiClient = action.params.clientId;
      const dataClients = [];
      dataChange.clients.forEach((client) => {
        const item = client;
        if (client.id === apiClient) {
          item.nextAppointment = newAppointment;
        }
        dataClients.push(item);
      });
      dataChange.clients = dataClients;
      return {
        ...state,
        data: dataChange,
      };
    }
    case GET_TRANSACTIONS_BY_STYLIST_SUCCESS: {
      return {
        ...state,
        transactionLog: action.response,
      };
    }
    default:
      return state;
  }
};

export const getDeposits = nullBind(getAnyOrNull, 'data.depositListData');
const checkoutReducerWrapper = (state, action) => {
  const result = checkoutReducer(state, action);

  const { type } = asObject(action);
  const oldDeposits = getDeposits(result);
  const newDeposits = getDeposits(state);

  if (oldDeposits !== newDeposits) {
    L.debug('checkoutReducerWrapper()', type, ':', action, '+', oldDeposits, '->', newDeposits);
  }
  return result;
};

export default checkoutReducerWrapper;
